import {
  BaseUserProfile, IMarketingAnalyticsProvider,
  ISignupSource, KnownMediums, KnownSources,
} from 'fitbeat.models';
import moment from 'moment-timezone';
import { URLSearchParams } from 'url';
import { DbGymRepository, DbWorkoutRepository } from '../../persistence';

interface IActiveCampaignContact extends IActiveCampaignContactInfo {
  id: string;
}

interface IActiveCampaignContactInfo {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  fieldValues?: {
    field?: string | undefined;
    value?: string | undefined;
  }[];
}

interface IActiveCampaignContactWebhookInfo {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  fields?: {
    source?: string;
    medium?: string;
  };
}

export interface IActiveCampaignConfig {
  marketingAnalytics: {
    googleAnalyticsMeasurementId?: string;
    googleAnalyticsAPISecret?: string;
    activeCampaignAPISecret?: string;
    activeCampaignAccountId?: string;
    activeCampaignAccountEventKey?: string;
    facebookPixelId?: string;
    facebookConversionsAPIKey?: string;
  };
  webAppUrl: string;
  membershipPlansUrl: string;
}

export class ActiveCampaignProvider implements IMarketingAnalyticsProvider {
  public readonly name = 'Active Campaign';

  constructor(
    private config: IActiveCampaignConfig,
    private logger: any,
    private dbWorkoutRepository: DbWorkoutRepository,
    private fetch: any,
  ) {
  }

  public async logEvent(
    name: string,
    userProfile: BaseUserProfile,
    data?: any,
  ): Promise<void> {
    this.logger.info(`log event ${name} in active campaign`);

    const envConfigured = !!(
      this.config.marketingAnalytics.activeCampaignAccountId
      && this.config.marketingAnalytics.activeCampaignAccountEventKey
      && this.config.marketingAnalytics.activeCampaignAPISecret
    );
    if (!envConfigured) {
      return;
    }

    const customerData: IActiveCampaignContact = await this.syncCustomer(userProfile);
    await this.addToAppUsersList(customerData.id);
    await this.logTrackingEvent(name, userProfile, data);
  }

  public async syncCustomer(userProfile: BaseUserProfile): Promise<IActiveCampaignContact> {
    const nextWorkoutDate = await this.getNextWorkoutDate(userProfile.uid);
    const contact: IActiveCampaignContactInfo = {
      email: userProfile.email,
      firstName: userProfile.firstname,
      lastName: userProfile.lastname,
      phone: userProfile.mobileNumber,
      fieldValues: [
        {
          field: '30',
          value: userProfile.longTermGoal,
        },
        {
          field: '29',
          value: userProfile.gender,
        },
        {
          field: '26',
          value: userProfile.accountType,
        },
        {
          field: '25',
          value: userProfile.dateOfBirth,
        },
        {
          field: '48',
          value: userProfile.currentPlanStatus,
        },
        {
          ...(userProfile.signupSource && userProfile.signupSource.medium && {
            field: '32',
            value: userProfile.signupSource.medium,
          }),
        },
        {
          ...(userProfile.currency && {
            field: '45',
            value: userProfile.currency,
          }),
        },
        {
          ...(userProfile.signupSource && userProfile.signupSource.source && {
            field: '33',
            value: userProfile.signupSource.source,
          }),
        },
        {
          ...(userProfile.signupSource && userProfile.signupSource.campaign && {
            field: '34',
            value: userProfile.signupSource.campaign,
          }),
        },
        {
          field: '49',
          value: userProfile.continueSignupLink || this.getGymPlansLink(userProfile.gymName),
        },
        {
          field: '50',
          value: nextWorkoutDate ? nextWorkoutDate.timeISO : '',
        },
        {
          field: '65',
          value: nextWorkoutDate ? nextWorkoutDate.timeFormatted : '',
        },
        {
          field: '68',
          value: userProfile.stripeSubscriptionData?.contractEndDate
            ? new Date(userProfile.stripeSubscriptionData?.contractEndDate).toISOString() : '',
        },
        {
          field: '69',
          value: userProfile.stripeSubscriptionData?.subscriptionCancelsAt
            ? new Date(userProfile.stripeSubscriptionData?.subscriptionCancelsAt).toISOString() : '',
        },
        {
          field: '78',
          value: userProfile.gymName,
        },
      ],
    };
    const postBody = {
      contact,
    };
    const response = await this.postToActiveCampaignAPI('contact/sync', postBody);
    return response.contact;
  }

  public mapActiveCampaignContactToPreAccountProfile(
    activeCampaignContact: IActiveCampaignContactWebhookInfo,
    gymName: string,
  )
    : Partial<BaseUserProfile> {
    const signupSource: ISignupSource = {
      medium: activeCampaignContact.fields?.medium || KnownMediums.integration,
      source: activeCampaignContact.fields?.source || KnownSources.activecampaign,
    };

    if (!activeCampaignContact.email || !activeCampaignContact.first_name || !activeCampaignContact.last_name) {
      throw new Error(
        `Cannot create pre-account from Active Campaign: invalid email, first or lastname for contact with id ${
          activeCampaignContact.id}`,
      );
    }

    if (!gymName) {
      throw new Error(
        `Cannot create pre-account from Active Campaign: no gymName provided ${activeCampaignContact.id}`,
      );
    }

    const partialUserProfile: Partial<BaseUserProfile> = {
      email: activeCampaignContact.email,
      firstname: activeCampaignContact.first_name,
      lastname: activeCampaignContact.last_name,
      mobileNumber: activeCampaignContact.phone,
      gymName,
      signupSource,
    };

    return partialUserProfile;
  }

  private async logTrackingEvent(event: string, userProfile: BaseUserProfile, data?: any): Promise<void> {
    const postData: any = {
      actid: this.config.marketingAnalytics.activeCampaignAccountId,
      key: this.config.marketingAnalytics.activeCampaignAccountEventKey,
      event,
      eventdata: this.getEventDataString(data),
      visit: JSON.stringify({
        email: userProfile.email,
      }),
    };
    const url = 'https://trackcmp.net/event';
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(postData),
    };
    const res = await this.fetch(url, options);
    if (!res.ok) {
      throw new Error(`Failed to log tracking event in ${this.name} provider`);
    }
  }

  private getEventDataString(data?: any) {
    if (!data) {
      return '';
    }
    return JSON.stringify(data);
  }

  private async addToAppUsersList(activeCampaignUserId: string) {
    const listId = 13;
    const activeStatus = 1;

    const postBody = {
      contactList: {
        sourceId: 0,
        list: listId,
        contact: activeCampaignUserId,
        status: activeStatus,
      },
    };
    return this.postToActiveCampaignAPI('contactLists', postBody);
  }

  private async postToActiveCampaignAPI(path: string, postBody: any) {
    const url = `https://gym491.api-us1.com/api/3/${path}`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // eslint vs tslint rule difference, ignore tslint rule that breaks eslint rule
        /* tslint:disable object-literal-key-quotes */
        Accept: 'application/json',
        'Api-Token': this.config.marketingAnalytics.activeCampaignAPISecret!,
      },
      body: JSON.stringify(postBody),
    };
    const res = await this.fetch(url, options);
    if (!res.ok) {
      throw new Error(`Failed to post to Active Campaign ${path}`);
    }
    return res.json();
  }

  private async getNextWorkoutDate(uid: string): Promise<{ timeISO: string, timeFormatted: string } | null> {
    const nextWorkout = await this.dbWorkoutRepository.getNextWorkout(uid, new Date());
    if (nextWorkout && nextWorkout.startTimeOfFirstExercise && nextWorkout.gymName) {
      const nextWorkoutTimeISOString = nextWorkout.startTimeOfFirstExercise;
      const gym = DbGymRepository.getByName(nextWorkout.gymName);
      const nextWorkoutTimeFormatted = moment.tz(nextWorkoutTimeISOString, gym.timezone).format('D MMM HH:mm');
      return {
        timeISO: nextWorkoutTimeISOString,
        timeFormatted: nextWorkoutTimeFormatted,
      };
    }
    return null;
  }

  private getGymPlansLink(gymName: string | undefined): string {
    if (!gymName) {
      return '';
    }
    const gym = DbGymRepository.getByName(gymName);
    return `${this.config.membershipPlansUrl}/${gym.websiteSlug}#plans`;
  }
}
